/**
 * Builds the input for getProjectModel from a project as the store holds it -
 * the fully loaded one (project page) or one from the project list
 * (overview). Both have the same list shape for this purpose.
 */
import { TaskDto } from "../features/tasks/types";
import { ProjectModelInput, ProjectModelListInput } from "./projectModel";

type DateLike = string | Date | null | undefined;

export interface ModelListSource {
  id: string;
  name?: string;
  startDate?: DateLike;
  dueDate?: DateLike;
  positionInProject?: number;
  notes?: { noteId: string }[];
  finishedNotes?: { completedAt?: DateLike }[];
}

export interface ModelProjectSource {
  startDate?: DateLike;
  dueDate?: DateLike;
  createdAt: DateLike;
  Collections?: ModelListSource[];
  inboxCollection?: ModelListSource | null;
}

export const toMs = (date?: DateLike) =>
  date ? new Date(date).getTime() : undefined;

/** Lists in project order - the phases of the model follow it */
export const sortProjectLists = <L extends ModelListSource>(lists?: L[]) =>
  [...(lists ?? [])].sort(
    (a, b) => (a.positionInProject ?? 0) - (b.positionInProject ?? 0),
  );

const toListInput = (
  col: ModelListSource,
  tasks: TaskDto[],
  fallbackName: string,
): ProjectModelListInput => {
  // Tasks without access are not in the store but still count as open
  const open = (col.notes ?? []).map((n) =>
    tasks.find((task) => task.id === n.noteId),
  );
  return {
    id: col.id,
    name: col.name || fallbackName,
    start: toMs(col.startDate),
    end: toMs(col.dueDate),
    openEfforts: open.map((task) => task?.timeEffort),
    openDueDates: open.flatMap((task) =>
      task?.dueDate ? [new Date(task.dueDate).getTime()] : [],
    ),
    finishedAt: (col.finishedNotes ?? []).flatMap((f) =>
      f.completedAt ? [new Date(f.completedAt).getTime()] : [],
    ),
  };
};

export function getProjectModelInput(
  project: ModelProjectSource,
  tasks: TaskDto[],
  now: number,
  fallbackName: string,
): ProjectModelInput {
  return {
    now,
    start: toMs(project.startDate),
    end: toMs(project.dueDate),
    createdAt: toMs(project.createdAt) ?? now,
    lists: sortProjectLists(project.Collections).map((col) =>
      toListInput(col, tasks, fallbackName),
    ),
    inbox: project.inboxCollection
      ? toListInput(project.inboxCollection, tasks, fallbackName)
      : undefined,
  };
}
